import ExitToApp from '@mui/icons-material/ExitToApp';
import FormatSizeIcon from '@mui/icons-material/FormatSize';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import MicIcon from '@mui/icons-material/Mic';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import {
  Box,
  ClickAwayListener,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { useWidgetContext } from '@ptek/widget-core';
import {
  FlexColumn,
  FlexRow,
  FlexRowBetween,
} from '../../../components/LayoutBoxes';
import { FC, useCallback, useMemo, useRef, useState } from 'react';
import { shouldHideLogout } from '../../../config/auth';
import {
  FONT_SIZE_OPTIONS,
  type FontSize,
} from '../../../hooks/useFontSizeSettings';
import { AmbientScribeContext } from '../../../types';
import { getAutoLoginConfig, isStandaloneMode } from '../../../utils';
import { iconStyles } from '../../../styles/iconStyles';
import {
  MENU_WIDTH_PX,
  recorderStyles as styles,
} from '../styles/recorderStyles';

export type RecorderSettingsProps = {
  microphones: Array<{
    id: string;
    label: string;
    type: string;
    description: string;
  }>;
  selectedMicrophoneId: string;
  microphoneError: string | null;
  isLoadingMicrophones: boolean;
  onSelectMicrophone: (id: string) => void;
  onRetryMicrophones: () => void;
  selectedFontSize: FontSize;
  onSelectFontSize: (size: FontSize) => void;
  showLogout?: boolean;
  onLogout?: () => void;
};

const settingsButtonStyles = {
  button: (isOpen: boolean) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 0.2,
    cursor: 'pointer',
    px: '10px',
    py: '4px',
    borderRadius: '16px',
    border: '1px solid',
    borderColor: 'divider',
    bgcolor: isOpen ? 'custom.menuActive' : 'custom.backgroundAlt',
    color: 'text.secondary',
    '&:hover': {
      bgcolor: isOpen ? 'custom.menuActive' : 'action.hover',
    },
  }),
  icon: { fontSize: '20px', color: 'text.secondary' },
  chevron: { fontSize: '20px', color: 'text.secondary' },
};

const menuItemStyles = {
  selectable: {
    py: 1.5,
  },
  noMicrophone: {
    py: 1.5,
    pointerEvents: 'none' as const,
  },
};

export const RecorderSettings: FC<RecorderSettingsProps> = ({
  microphones,
  selectedMicrophoneId,
  microphoneError,
  isLoadingMicrophones,
  onSelectMicrophone,
  onRetryMicrophones,
  selectedFontSize,
  onSelectFontSize,
  showLogout,
  onLogout,
}) => {
  const { widgetContext } = useWidgetContext<AmbientScribeContext>();
  const [settingsAnchor, setSettingsAnchor] = useState<HTMLElement | null>(
    null
  );
  const [microphoneSubmenuAnchor, setMicrophoneSubmenuAnchor] =
    useState<HTMLElement | null>(null);
  const [fontSizeSubmenuAnchor, setFontSizeSubmenuAnchor] =
    useState<HTMLElement | null>(null);

  const triggerRef = useRef<HTMLDivElement | null>(null);

  const menuPaperStyles = useMemo(
    () => ({
      main: {
        bgcolor: 'background.paper',
        width: MENU_WIDTH_PX,
        minWidth: MENU_WIDTH_PX,
        maxWidth: MENU_WIDTH_PX,
        overflow: 'hidden',
        boxShadow: 'custom.menuShadow',
        borderRadius: 1,
      },
      submenu: {
        bgcolor: 'background.paper',
        boxShadow: 'custom.menuShadow',
        borderRadius: 1,
      },
    }),
    []
  );

  const handleCloseSettings = useCallback(() => {
    setSettingsAnchor(null);
    setMicrophoneSubmenuAnchor(null);
    setFontSizeSubmenuAnchor(null);
  }, []);

  const handleToggleSettings = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (settingsAnchor) {
        handleCloseSettings();
        return;
      }
      setSettingsAnchor(e.currentTarget);
    },
    [settingsAnchor, handleCloseSettings]
  );

  const handleSubmenuClick = useCallback(
    (
      e: React.MouseEvent<HTMLElement>,
      setter: (el: HTMLElement | null) => void
    ) => {
      e.stopPropagation();
      setter(e.currentTarget);
    },
    []
  );

  const handleClickAway = useCallback(() => {
    if (settingsAnchor || microphoneSubmenuAnchor || fontSizeSubmenuAnchor) {
      handleCloseSettings();
    }
  }, [
    settingsAnchor,
    microphoneSubmenuAnchor,
    fontSizeSubmenuAnchor,
    handleCloseSettings,
  ]);

  const renderMicrophoneItems = useMemo(() => {
    if (isLoadingMicrophones) {
      return (
        <MenuItem disabled sx={menuItemStyles.noMicrophone}>
          <Typography variant='body2'>Loading microphones...</Typography>
        </MenuItem>
      );
    }

    if (microphoneError) {
      return (
        <>
          <MenuItem disabled sx={menuItemStyles.noMicrophone}>
            <Typography variant='body2' sx={styles.microphoneErrorText}>
              {microphoneError}
            </Typography>
          </MenuItem>
          <MenuItem
            onClick={() => {
              setMicrophoneSubmenuAnchor(null);
              onRetryMicrophones();
            }}
            sx={menuItemStyles.selectable}
          >
            <Typography variant='body2' sx={styles.microphoneRetryText}>
              Try Again
            </Typography>
          </MenuItem>
        </>
      );
    }

    if (microphones.length === 0) {
      return (
        <MenuItem disabled sx={menuItemStyles.noMicrophone}>
          <Typography variant='body2'>No microphones found</Typography>
        </MenuItem>
      );
    }

    return microphones.map((m) => (
      <MenuItem
        key={m.id}
        onClick={() => {
          onSelectMicrophone(m.id);
          setMicrophoneSubmenuAnchor(null);
        }}
        selected={selectedMicrophoneId === m.id}
        sx={menuItemStyles.selectable}
      >
        <Box sx={styles.microphoneRow}>
          <MicIcon sx={iconStyles.sm} color='action' />
          <Box sx={styles.microphoneContent}>
            <Typography
              variant='menuItemPrimary'
              sx={styles.microphonePrimaryText}
            >
              {m.label}
            </Typography>
            <Typography
              variant='menuItemSecondary'
              sx={styles.microphoneSecondaryText}
            >
              {m.type} • {m.description}
            </Typography>
          </Box>
        </Box>
      </MenuItem>
    ));
  }, [
    isLoadingMicrophones,
    microphoneError,
    microphones,
    selectedMicrophoneId,
    onSelectMicrophone,
    onRetryMicrophones,
  ]);

  const renderFontSizeItems = useMemo(
    () =>
      FONT_SIZE_OPTIONS.map((f) => {
        // Calculate actual font size as it appears in the app (base size 0.75rem * multiplier)
        const actualFontSize = `${0.75 * f.multiplier}rem`;

        return (
          <MenuItem
            key={f.id}
            onClick={() => {
              onSelectFontSize(f.id as FontSize);
              setFontSizeSubmenuAnchor(null);
            }}
            selected={selectedFontSize === f.id}
            sx={menuItemStyles.selectable}
          >
            <FlexColumn>
              <Typography
                variant='menuItemPrimary'
                sx={{ whiteSpace: 'nowrap', fontSize: actualFontSize }}
              >
                {f.displayName}
              </Typography>
              <Typography
                variant='menuItemSecondary'
                sx={{ whiteSpace: 'nowrap' }}
              >
                {f.description}
              </Typography>
            </FlexColumn>
          </MenuItem>
        );
      }),
    [selectedFontSize, onSelectFontSize]
  );

  const renderMenuItemWithIcon = (
    icon: React.ReactNode,
    text: string,
    onClick: (e: React.MouseEvent<HTMLElement>) => void
  ) => (
    <MenuItem onClick={onClick}>
      <FlexRowBetween>
        <FlexRow gap={1}>
          {icon}
          <Typography variant='menuItemPrimary'>{text}</Typography>
        </FlexRow>
        <KeyboardArrowRightIcon sx={iconStyles.md} color='action' />
      </FlexRowBetween>
    </MenuItem>
  );

  const renderLogoutMenuItem = () => {
    if (!showLogout || !onLogout) return null;

    // Hide logout if host seamless auth is configured without fallback
    if (shouldHideLogout()) return null;

    // Hide logout if standalone mode with auto-login is configured
    if (
      widgetContext.context &&
      isStandaloneMode(widgetContext.context) &&
      getAutoLoginConfig(widgetContext.context)
    ) {
      return null;
    }

    return (
      <MenuItem onClick={onLogout}>
        <FlexRow gap={1}>
          <ExitToApp sx={iconStyles.md} color='action' />
          <Typography variant='menuItemPrimary'>Logout</Typography>
        </FlexRow>
      </MenuItem>
    );
  };

  return (
    <>
      <ClickAwayListener onClickAway={handleClickAway} mouseEvent='onClick'>
        <Box
          ref={triggerRef}
          data-testid='recorder-settings'
          sx={settingsButtonStyles.button(Boolean(settingsAnchor))}
          role='button'
          tabIndex={0}
          onClick={handleToggleSettings}
        >
          <SettingsOutlinedIcon sx={settingsButtonStyles.icon} />
          <KeyboardArrowDownIcon sx={settingsButtonStyles.chevron} />
        </Box>
      </ClickAwayListener>

      <Menu
        anchorEl={settingsAnchor}
        open={Boolean(settingsAnchor)}
        onClose={handleCloseSettings}
        {...styles.menuMainOrigins}
        disablePortal={false}
        hideBackdrop
        disableScrollLock
        PaperProps={{ sx: menuPaperStyles.main }}
        MenuListProps={{ sx: styles.menuList }}
        sx={styles.menu}
      >
        {renderMenuItemWithIcon(
          <MicIcon sx={iconStyles.md} color='action' />,
          'Microphone Selection',
          (e) => handleSubmenuClick(e, setMicrophoneSubmenuAnchor)
        )}
        {renderMenuItemWithIcon(
          <FormatSizeIcon sx={iconStyles.md} color='action' />,
          'Font Size',
          (e) => handleSubmenuClick(e, setFontSizeSubmenuAnchor)
        )}
        {renderLogoutMenuItem()}
      </Menu>

      <Menu
        anchorEl={microphoneSubmenuAnchor}
        open={Boolean(microphoneSubmenuAnchor)}
        onClose={() => setMicrophoneSubmenuAnchor(null)}
        {...styles.menuSubmenuOrigins}
        disablePortal={false}
        PaperProps={{
          sx: { ...menuPaperStyles.submenu, minWidth: MENU_WIDTH_PX },
        }}
        MenuListProps={{ sx: styles.menuList }}
        sx={styles.menu}
      >
        {renderMicrophoneItems}
      </Menu>

      <Menu
        anchorEl={fontSizeSubmenuAnchor}
        open={Boolean(fontSizeSubmenuAnchor)}
        onClose={() => setFontSizeSubmenuAnchor(null)}
        {...styles.menuSubmenuOrigins}
        disablePortal={false}
        PaperProps={{
          sx: {
            ...menuPaperStyles.submenu,
            width: 'max-content',
            minWidth: 'unset',
          },
        }}
        MenuListProps={{ sx: styles.menuList }}
        sx={styles.menu}
      >
        {renderFontSizeItems}
      </Menu>
    </>
  );
};
